import { MapPinIcon } from 'lucide-react';
import { cn } from '@noor/lib';
import type { OfferTone } from '@/entities/order';
import { toneFill, toneInk } from '../lib/offer-colors';

interface OfferBadgeFaceProps {
  tone: OfferTone;
  /** Distance printed inside the badge. */
  km: number;
  opacity: number;
  selected?: boolean | undefined;
  /** Being offered to this courier right now — adds a pulsing halo. */
  active?: boolean | undefined;
}

/**
 * The round courier badge: filled with its outcome hue, distance inside.
 * Purely visual (no handlers) so Leaflet can render it to static markup and
 * ymaps can wrap it in a real `<button>`.
 */
export function OfferBadgeFace({
  tone,
  km,
  opacity,
  selected,
  active,
}: OfferBadgeFaceProps) {
  return (
    <span
      className="relative grid size-10 place-items-center transition-opacity duration-200"
      style={{ opacity }}
    >
      {active ? (
        <span
          aria-hidden
          className="absolute inset-0 animate-ping rounded-full"
          style={{ backgroundColor: toneFill(tone), opacity: 0.45 }}
        />
      ) : null}
      <span
        className={cn(
          'relative grid size-10 place-items-center rounded-full border-2 border-white text-[10px] font-semibold tabular-nums shadow-md transition-transform duration-150',
          selected && 'scale-125 ring-4 ring-primary/40',
        )}
        style={{ backgroundColor: toneFill(tone), color: toneInk(tone) }}
      >
        {km.toFixed(1)}
      </span>
    </span>
  );
}

/** The order's pickup pin, with the order number underneath. */
export function OriginPinFace({ label }: { label?: string | undefined }) {
  return (
    <span className="relative grid size-9 place-items-center rounded-full border-[3px] border-white bg-primary text-primary-foreground shadow-lg">
      <MapPinIcon className="size-4.5" />
      {label ? (
        <span className="absolute top-full mt-1 rounded-md bg-foreground px-1.5 py-0.5 text-[10px] leading-none font-semibold whitespace-nowrap text-background shadow-sm">
          {label}
        </span>
      ) : null}
    </span>
  );
}
